import { useContext, useEffect, useMemo, useRef } from "react"
import { useShallow } from "zustand/react/shallow"

import { WidgetModskillAppChartUI } from "./ui"
import { IWidgetModskillAppChartActions, IWidgetModskillAppChartUI } from "./types"

import { getStatsAppDaysModskill as requestStatsAppDaysModskill } from "@api/dashboard/api"
import { useMonitoring } from "@api/dashboard/state"
import { CommonStatsAppType, IStatsAppDays, IStatsAppMonth, IStatsAppYear } from "@api/dashboard/state.types"

import { useValue } from "@hooks/useValue"

import { NotificationContext, notifyOpen } from "@ui/notification/notification.provider"


const PREVIOUS_MONTH_START_DAY = 15

const getPreviousMonthPeriod = (year: number, month: number) => {
    const previousMonthDate = new Date(year, month - 2, 1)

    return {
        year: previousMonthDate.getFullYear(),
        month: previousMonthDate.getMonth() + 1,
    }
}

const getPreviousMonthDaysFromFifteenth = (previousItems: IStatsAppDays[] = []) => {
    return previousItems.filter((item) => item.day >= PREVIOUS_MONTH_START_DAY)
}

const mergeItemsWithPreviousMonthTail = (currentItems: IStatsAppDays[] = [], previousItems: IStatsAppDays[] = []) => {
    return [...currentItems, ...getPreviousMonthDaysFromFifteenth(previousItems)]
}


/** Контейнер виджета - Статистика заявок ModSkill Chart  */
export const WidgetModskillAppChartContainer = ({ getMonthName }: IWidgetModskillAppChartActions) => {
    const notify = useContext(NotificationContext)

    /** Текущий год статистики */
    const txtCurrentYear = useValue<string>('')
    /** Текущий год статистики */
    const txtCurrentMonth = useValue<string>('')
    /** Таймер, для автоматического обновления основного списка */
    const intervalRef = useRef<NodeJS.Timeout | null>(null)
    const dayRequestVersionRef = useRef(0)

    const itemsYear = useValue<IStatsAppYear[]>([])
    const itemsMonth = useValue<IStatsAppMonth[]>([])
    const itemsDays = useValue<IStatsAppDays[]>([])
    const showStatisticsData = useValue<boolean>(localStorage.getItem('msChartShowStatisticsData') === 'true')


    /** Тип статистики */
    const typeStats = useValue<'day' | 'month' | 'year'>(
        (localStorage.getItem('msChartTypeDetailStatApp') as 'day' | 'month' | 'year') || 'day'
    )
    /** Выбранный ГОД отображаемой статистики (Только для typeStats = day) */
    const yearDetailStatsApp = useValue<string>(
        localStorage.getItem('msChartYearDetailStatApp') || `${new Date().getFullYear()}`
    )
    /** Выбранный МЕСЯЦ отображаемой статистики (Только для typeStats = day) */
    const monthDetailStatsApp = useValue<string>(
        localStorage.getItem('msChartMonthDetailStatApp') || `${new Date().getMonth() + 1}`
    )

    // =================================================================================

    // Хранилище страницы - Мониторинг
    const { 
        actionGetStatsAppYearsModskill, actionGetStatsAppMonthModskill, actionGetStatsAppDaysModskill,
    } = useMonitoring(useShallow((state) => ({
        actionGetStatsAppYearsModskill: state.actionGetStatsAppYearsModskill,
        actionGetStatsAppMonthModskill: state.actionGetStatsAppMonthModskill,
        actionGetStatsAppDaysModskill: state.actionGetStatsAppDaysModskill,
    })))

    const loadDayStatistics = async (year: number, month: number, isLoadingDisplay: boolean = true) => {
        const requestVersion = ++dayRequestVersionRef.current
        const response = await actionGetStatsAppDaysModskill(year, month, isLoadingDisplay)

        if (response.status === 200 && response.data?.items) {
            if (requestVersion !== dayRequestVersionRef.current) return

            const previousMonthPeriod = getPreviousMonthPeriod(year, month)
            const previousMonthResponse = await requestStatsAppDaysModskill(previousMonthPeriod.year, previousMonthPeriod.month, !isLoadingDisplay)
            if (requestVersion !== dayRequestVersionRef.current) return

            const previousMonthItems = previousMonthResponse.status === 200 && previousMonthResponse.data?.items
                ? previousMonthResponse.data.items
                : []

            itemsDays.setValue(mergeItemsWithPreviousMonthTail(response.data.items, previousMonthItems))
        }

        if (response.status !== 200 && response.status !== 500) {
            notify && notifyOpen(response.message, 'error', 2000, notify)
        }
    }
    
    // =================================================================================

    useEffect(() => { // Первичная загрузка статистики
        actionGetStatsAppYearsModskill().then((res) => {
            if (res.status === 200 && res.data?.items) {
                itemsYear.setValue(res.data.items)
            }

            if (res.status !== 200 && res.status !== 500) {
                notify && notifyOpen(res.message, 'error', 2000, notify)
            }
        })

        actionGetStatsAppMonthModskill().then((res) => {
            if (res.status === 200 && res.data?.items) {
                itemsMonth.setValue(res.data.items)
            }

            if (res.status !== 200 && res.status !== 500) {
                notify && notifyOpen(res.message, 'error', 2000, notify)
            }
        })

    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { // Загрузка статистики при смене ГОДА или МЕСЯЦА
        if (typeStats.value !== 'day') return

        /** Номер месяца для загрузки статистики */
        let currentMonth: number = Number(monthDetailStatsApp.value)

        /** Выбираем все месяца по выбранному ГОДУ */
        const listMonthsOnYear = itemsMonth.value.filter((item) => item.year === Number(yearDetailStatsApp.value))
        /** По выбранному номеру месяца, проверяем существование этого месяца */
        const listMonthOnMonth = listMonthsOnYear.filter((item) => item.month === currentMonth)

        if (listMonthOnMonth.length === 0 && listMonthsOnYear.length > 0) {
            currentMonth = listMonthsOnYear[0].month
            monthDetailStatsApp.setValue(`${listMonthsOnYear[0].month}`)
        }

        // Загружаем статистику
        loadDayStatistics(Number(yearDetailStatsApp.value), currentMonth)
    }, [typeStats.value, yearDetailStatsApp.value, monthDetailStatsApp.value, itemsMonth.value]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { // Записываем значение в LocalStorage при изменении typeStats
        localStorage.setItem('msChartTypeDetailStatApp', typeStats.value)
    }, [typeStats.value]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { // Записываем значение в LocalStorage при изменении yearDetailStatsApp
        txtCurrentYear.setValue(`${yearDetailStatsApp.value}`)
        localStorage.setItem('msChartYearDetailStatApp', yearDetailStatsApp.value)
    }, [yearDetailStatsApp.value]) // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect(() => { // Записываем значение в LocalStorage при изменении monthDetailStatsApp
        txtCurrentMonth.setValue(getMonthName(Number(monthDetailStatsApp.value)))
        localStorage.setItem('msChartMonthDetailStatApp', monthDetailStatsApp.value)
    }, [monthDetailStatsApp.value]) // eslint-disable-line react-hooks/exhaustive-deps    

    useEffect(() => {
        localStorage.setItem('msChartShowStatisticsData', `${showStatisticsData.value}`)
    }, [showStatisticsData.value]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        intervalRef.current = setInterval(() => {
            loadDataAutomatically()
        }, 30000)

        return () => {
            dayRequestVersionRef.current += 1
            if (intervalRef.current) clearInterval(intervalRef.current)
        }
    }, [typeStats.value, yearDetailStatsApp.value, monthDetailStatsApp.value]) // eslint-disable-line react-hooks/exhaustive-deps

    // =================================================================================

    /** Общее количество заявок по статусам по всем годам и месяцам */
    const totalsYears = useMemo(() => {
        if (!itemsYear.value || itemsYear.value.length === 0) return undefined

        return (itemsYear.value as IStatsAppYear[]).reduce<CommonStatsAppType>((acc, curr) => ({
            new: acc.new + curr.new,
            delayCall: acc.delayCall + curr.delayCall,
            contracts: acc.contracts + curr.contracts,
            cancel: acc.cancel + curr.cancel,
            notClient: acc.notClient + curr.notClient,
            double: acc.double + curr.double,
            ignore: acc.ignore + curr.ignore,
            total: acc.total + curr.total,
        }), {
            new: 0, delayCall: 0, contracts: 0, cancel: 0,
            notClient: 0, double: 0, ignore: 0, total: 0
        })
    }, [itemsYear.value])

    /** Общее количество заявок по статусам по всем годам и месяцам */
    const totalsDays = useMemo(() => {
        if (!itemsDays.value || itemsDays.value.length === 0) return undefined

        return (itemsDays.value as IStatsAppDays[]).reduce<CommonStatsAppType>((acc, curr) => ({
            new: acc.new + curr.new,
            delayCall: acc.delayCall + curr.delayCall,
            contracts: acc.contracts + curr.contracts,
            cancel: acc.cancel + curr.cancel,
            notClient: acc.notClient + curr.notClient,
            double: acc.double + curr.double,
            ignore: acc.ignore + curr.ignore,
            total: acc.total + curr.total,
        }), {
            new: 0, delayCall: 0, contracts: 0, cancel: 0,
            notClient: 0, double: 0, ignore: 0, total: 0
        })
    }, [itemsDays.value])

    // =================================================================================


    /** Загрузка данных */
    const loadDataAutomatically = () => {
        switch (typeStats.value) {
            case 'day': {
                loadDayStatistics(Number(yearDetailStatsApp.value), Number(monthDetailStatsApp.value), false)
                break
            }

            case 'month': {
                actionGetStatsAppMonthModskill(false).then((res) => {
                    if (res.status === 200) {
                        if (res.data?.items) itemsMonth.setValue(res.data?.items)
                    }
                })
                break
            }

            case 'year': {
                actionGetStatsAppYearsModskill(false).then((res) => {
                    if (res.status === 200) {
                        if (res.data?.items) itemsYear.setValue(res.data?.items)
                    }
                })
                break
            }
        }
    }

    // =================================================================================

    /** Свойства передаваемые в UI компоненту */
    const propsToUI: IWidgetModskillAppChartUI = {
        txtCurrentYear, txtCurrentMonth,
        showStatisticsData,

        totalsYears, totalsDays,

        typeStats, yearDetailStatsApp, monthDetailStatsApp,
        itemsYear: itemsYear.value, itemsMonth: itemsMonth.value, itemsDays: itemsDays.value,

        getMonthName: getMonthName,
    }

    return <>
        <WidgetModskillAppChartUI {...propsToUI} />
    </>
}